/**
 * Display formatting, in one place.
 *
 * Indian conventions are the default: `DD MMM YYYY` dates and the lakh/crore
 * grouping that `en-IN` gives us for free (₹12,50,000 — not ₹1,250,000).
 */

const LOCALE = 'en-IN';

/** `2026-07-31` → `31 Jul 2026`. Returns an em dash for a missing value. */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/**
 * A calendar month — "Oct 2026", or "Oct" for a chart axis.
 *
 * Read in UTC: the server sends a month as its first day (`2026-10-01`), and
 * reading that midnight in a zone behind UTC would name the month before.
 */
export function formatMonth(value: string, withYear = true): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(LOCALE, {
    month: 'short',
    ...(withYear && { year: 'numeric' }),
    timeZone: 'UTC',
  }).format(date);
}

/** Date plus time, for audit trails and "last signed in". */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * `14:30` → `02:30 PM`. Clock times are stored as `HH:mm` in 24-hour form.
 *
 * Built by hand rather than through `Intl`, which writes the meridiem in
 * lower case for `en-IN` on some engines and upper case on others — a timetable
 * that reads "09:00 am" in one browser and "09:00 AM" in the next.
 */
export function formatClockTime(value: string | null | undefined): string {
  if (!value) return '—';

  const [hours, minutes] = value.split(':').map(Number);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return '—';
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return '—';

  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  const meridiem = hours < 12 ? 'AM' : 'PM';

  return `${String(hour12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${meridiem}`;
}

/**
 * `09:00`–`16:00` → `09:00 AM - 04:00 PM`.
 *
 * An entry with no start time is an all-day one — a holiday runs from the
 * moment the gates would have opened, not from 12:00 AM.
 */
export function formatTimeRange(
  start: string | null | undefined,
  end: string | null | undefined,
  allDayLabel = 'All day',
): string {
  if (!start) return allDayLabel;
  return end ? `${formatClockTime(start)} - ${formatClockTime(end)}` : formatClockTime(start);
}

/** `125000` → `₹1,25,000`. Amounts arrive as strings from Decimal columns. */
export function formatCurrency(
  value: number | string | null | undefined,
  currency = 'INR',
): string {
  if (value === null || value === undefined || value === '') return '—';
  const amount = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(amount)) return '—';

  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
}

const WORDS_UNDER_20 = [
  'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen',
];

const WORDS_TENS = [
  '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety',
];

/** 0–99. */
function underHundred(value: number): string {
  if (value < 20) return WORDS_UNDER_20[value];
  const ones = value % 10;
  const tens = WORDS_TENS[Math.floor(value / 10)];
  return ones === 0 ? tens : `${tens} ${WORDS_UNDER_20[ones]}`;
}

/** 0–999. */
function underThousand(value: number): string {
  const hundreds = Math.floor(value / 100);
  const rest = value % 100;
  if (hundreds === 0) return underHundred(rest);
  const head = `${WORDS_UNDER_20[hundreds]} Hundred`;
  return rest === 0 ? head : `${head} ${underHundred(rest)}`;
}

/** Whole rupees, grouped the Indian way — thousand, lakh, crore. */
function rupeesInWords(value: number): string {
  if (value < 1000) return underThousand(value);

  if (value < 100_000) {
    const thousands = Math.floor(value / 1000);
    const rest = value % 1000;
    return `${underThousand(thousands)} Thousand${rest === 0 ? '' : ` ${underThousand(rest)}`}`;
  }

  if (value < 10_000_000) {
    const lakhs = Math.floor(value / 100_000);
    const rest = value % 100_000;
    return `${underHundred(lakhs)} Lakh${rest === 0 ? '' : ` ${rupeesInWords(rest)}`}`;
  }

  // Recursive rather than capped: `DECIMAL(12, 2)` reaches ninety-nine thousand
  // crore, and a receipt that silently stopped counting would be worse than a
  // long line of words.
  const crores = Math.floor(value / 10_000_000);
  const rest = value % 10_000_000;
  return `${rupeesInWords(crores)} Crore${rest === 0 ? '' : ` ${rupeesInWords(rest)}`}`;
}

/**
 * `14000` → `Rupees Fourteen Thousand only`.
 *
 * What every money receipt in India carries under the figures, and the reason
 * it does: a digit can be added in front of a printed total, a line of words
 * cannot. Rounded to the paise before it is read, so a value that arrived as a
 * string off a `DECIMAL(12, 2)` column can never be spelled out as one paise
 * short of itself.
 */
export function formatAmountInWords(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const amount = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(amount) || amount < 0) return '—';

  const paiseTotal = Math.round(amount * 100);
  const rupees = Math.floor(paiseTotal / 100);
  const paise = paiseTotal % 100;

  const words = rupeesInWords(rupees);
  return paise === 0
    ? `Rupees ${words} only`
    : `Rupees ${words} and ${underHundred(paise)} Paise only`;
}

/** Thousands separators in the Indian grouping. */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(LOCALE).format(value);
}

/** The steps a "how long ago" line is rounded to, largest unit first. */
const AGO_STEPS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 86_400_000],
  ['month', 30 * 86_400_000],
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
];

/**
 * `2026-08-19T04:10:00Z` → `5 hr ago`, for a feed of recent things.
 *
 * Rounded to the largest unit that fits, which is how somebody reads a list of
 * notices — "2 days ago" is the answer, never "51 hours ago". Anything under a
 * minute is "just now" rather than a count of seconds that is wrong by the time
 * it is painted.
 */
export function formatTimeAgo(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';

  const elapsed = Date.now() - date.getTime();
  // A clock a few seconds behind the server's must not produce "in 3 seconds".
  if (elapsed < 60_000) return 'just now';

  const formatter = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'always', style: 'short' });

  for (const [unit, size] of AGO_STEPS) {
    if (elapsed >= size) return formatter.format(-Math.floor(elapsed / size), unit);
  }

  return 'just now';
}


/**
 * `184320` → `180 KB`, `5452595` → `5.2 MB`.
 *
 * For shared components; `features/parent/utils` and
 * `features/teacher/activities/utils` each still carry their own copy.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * `275` → `4:35`, `3730` → `1:02:10` — a video's length as its player shows it.
 *
 * Seconds in, never minutes: a recorded lesson's length is read from the video
 * itself, and a video is 4:35 long, not "5 mins". Null for none.
 */
export function formatVideoLength(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || seconds <= 0) return null;

  const whole = Math.round(seconds);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const rest = String(whole % 60).padStart(2, '0');

  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}`
    : `${minutes}:${rest}`;
}

/** "42 mins", "1 hr 25 mins" — minutes as a person would say them. Null for none. */
export function formatDuration(minutes: number | null): string | null {
  if (minutes === null || minutes <= 0) return null;
  if (minutes < 60) return `${minutes} mins`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} mins`;
}

/**
 * "anjali sharma" → "Anjali Sharma"; "d'souza" → "D'Souza"; "mary-jane" → "Mary-Jane".
 *
 * Only ever *raises* a first letter — the rest of each word is left exactly as
 * typed, so "McDonald" and "IIT" survive. For display: names arrive in whatever
 * case the office keyed them in, and a register should not read half in lower case.
 */
export function capitalizeWords(value: string): string {
  return value.replace(/(^|[\s\-'(])(\p{Ll})/gu, (_, lead: string, letter: string) => lead + letter.toUpperCase());
}
